// Walk segments: the model, the maths, and the seeded table.
// Spec 5.4, 5.5, 7.1. Imported by both the worker and the web app so the
// segment ID scheme can never drift between them.

export type SegmentKind = 'street' | 'station-entry' | 'interchange' | 'exit';

export interface WalkSegment {
  id: string;
  label: string;
  kind: SegmentKind;
  seedSeconds: number; // our first guess
  overrideSeconds?: number; // user typed a number, this always wins
  samples: number[]; // recent measured walks, seconds
  peakDeltaSeconds?: number; // added 07:30-09:30 and 17:00-19:00
  lastUsedAt?: string;
  pinned?: boolean;
}

export interface Profile {
  // 1.0 normal, 1.25 in a hurry, 0.8 carrying bags or tired.
  // Higher means faster, so it divides. Spec 5.4.
  paceFactor: number;
  bufferMinutes: number; // spare minutes before a departure. Default 2
  stepFreeRequired: boolean;
}

export const DEFAULT_PROFILE: Profile = {
  paceFactor: 1.0,
  bufferMinutes: 2,
  stepFreeRequired: false,
};

export const PACE_STEPS = [
  { label: 'Steady', factor: 0.8 },
  { label: 'Normal', factor: 1.0 },
  { label: 'Rushing', factor: 1.25 },
] as const;

// Spec 7.1 guardrails: nothing below 30 seconds or above 30 minutes.
export const MIN_SEGMENT_SECONDS = 30;
export const MAX_SEGMENT_SECONDS = 30 * 60;

export const clampSeconds = (s: number): number =>
  Math.min(MAX_SEGMENT_SECONDS, Math.max(MIN_SEGMENT_SECONDS, Math.round(s)));

export function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

// Spec 5.4: 07:30-09:30 and 17:00-19:00.
export function isPeak(now: Date): boolean {
  const m = now.getHours() * 60 + now.getMinutes();
  return (m >= 7 * 60 + 30 && m <= 9 * 60 + 30) || (m >= 17 * 60 && m <= 19 * 60);
}

/**
 * Resolution order, spec 5.4:
 *   1. An explicit user number always wins.
 *   2. Otherwise the median of the last seven measured walks, but only
 *      once there are at least three, so one odd walk cannot take over.
 *   3. Otherwise our seed.
 * Then add the peak surcharge, then scale by how fast you are moving today.
 */
export function segmentSeconds(seg: WalkSegment, now: Date, profile: Profile): number {
  const base =
    seg.overrideSeconds ??
    (seg.samples.length >= 3 ? median(seg.samples.slice(-7)) : seg.seedSeconds);

  const peak = isPeak(now) ? (seg.peakDeltaSeconds ?? 0) : 0;

  return (base + peak) / profile.paceFactor;
}

// Spec 7.1: how sure are we about this number.
export type Confidence = 'seed' | 'learning' | 'settled';

export function confidenceOf(seg: WalkSegment): Confidence {
  if (seg.overrideSeconds !== undefined) return 'settled';
  if (seg.samples.length >= 3) return 'settled';
  if (seg.samples.length > 0) return 'learning';
  return 'seed';
}

// Spec 7.1: never silently overwrite an override, but do speak up once the
// measured median has drifted a long way from it.
export const DRIFT_PROMPT_SECONDS = 90;

export function driftPrompt(seg: WalkSegment): { measured: number } | null {
  if (seg.overrideSeconds === undefined || seg.samples.length < 3) return null;
  const measured = median(seg.samples.slice(-7));
  return Math.abs(measured - seg.overrideSeconds) > DRIFT_PROMPT_SECONDS
    ? { measured }
    : null;
}

export type Catchability = 'comfortable' | 'tight' | 'missed';

/**
 * Spec 5.5. Note that `walkMinutes` must already have the pace factor
 * applied by segmentSeconds. The spec's own snippet divided by a
 * `speedFactor` a second time here, which would apply the pace dial twice
 * and make a rushing user look far more capable than they are. See the
 * correction logged in spec section 16.
 */
export function catchability(
  walkMinutes: number,
  accessPenaltyMinutes: number,
  minutesUntilDeparture: number,
  profile: Profile
): Catchability {
  const needed = walkMinutes + accessPenaltyMinutes + profile.bufferMinutes;
  if (minutesUntilDeparture < needed) return 'missed';
  if (minutesUntilDeparture < needed + 3) return 'tight';
  return 'comfortable';
}

// ---------------------------------------------------------------------
// Segment identity
// ---------------------------------------------------------------------

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/\(.*?\)/g, '')
    .replace(/\b(underground|rail|station|dlr)\b/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32) || 'here';

/** A street walk the journey planner gave us. Spec 5.4, kind 'street'. */
export const streetSegmentId = (from: string, to: string) =>
  `street:${slug(from)}>${slug(to)}`;

/** Getting from one platform to another inside a station. Kind 'interchange'. */
export const interchangeSegmentId = (station: string, fromLine: string, toLine: string) =>
  `ix:${slug(station)}:${slug(fromLine)}>${slug(toLine)}`;

/** Getting out at the far end and to the actual door. Kind 'exit'. */
export const exitSegmentId = (placeId: string) => `exit:${placeId}`;

// ---------------------------------------------------------------------
// Seeded segments, spec 5.4
// ---------------------------------------------------------------------

const seg = (
  id: string,
  label: string,
  kind: SegmentKind,
  minutes: number,
  pinned = true
): WalkSegment => ({
  id,
  label,
  kind,
  seedSeconds: minutes * 60,
  samples: [],
  pinned,
});

// The two marked BOLD in the spec are the whole reason this feature exists:
// on the Charing Cross route the app has to choose between them, and it
// cannot choose correctly until it knows which one you are faster through.
export const SEED_SEGMENTS: WalkSegment[] = [
  seg('strand40>chx-nr', '40 Strand to Charing Cross NR entrance', 'street', 3),
  seg('strand40>chx-tube', '40 Strand to Charing Cross tube, Strand entrance', 'street', 3),
  seg('strand40>emb', '40 Strand to Embankment, via Villiers Street', 'street', 5),
  seg('chx-nr:entry>platform', 'Charing Cross gateline to the Southeastern platform', 'station-entry', 3),
  seg('chx-tube:entry>bakerloo-sb', 'Charing Cross tube entrance to Bakerloo southbound', 'station-entry', 3),
  seg('chx-tube:entry>northern-sb', 'Charing Cross tube entrance to Northern southbound', 'station-entry', 3),
  seg('emb:entry>district-wb', 'Embankment entrance to District westbound', 'station-entry', 3),
  seg('emb:entry>bakerloo-sb', 'Embankment entrance to Bakerloo southbound', 'station-entry', 2),
  seg('wae:platform>wat-concourse', 'Waterloo East platform to Waterloo main concourse', 'interchange', 6),
  seg('wat:bakerloo-platform>concourse', 'Waterloo Bakerloo platform to main concourse', 'interchange', 4),
  seg('wat:northern-platform>concourse', 'Waterloo Northern platform to main concourse', 'interchange', 4),
  seg('wat:concourse>platform-1-6', 'Waterloo concourse to the low numbered platforms', 'interchange', 3),
  seg('wat:concourse>platform-7-19', 'Waterloo concourse to the middle platforms', 'interchange', 4),
  seg('clj:interchange', 'Clapham Junction platform to platform', 'interchange', 4),
  // Spec 15.5: one exit segment per saved place. Richmond station to
  // 48 Sheen Park is 320 m, so four minutes is the seed. Yours to correct.
  seg('exit:richmond', 'Richmond platform to 48 Sheen Park', 'exit', 4),
  seg('exit:strand40', 'Arrival point to your desk at 40 Strand', 'exit', 3),
  // Spec 13.2, desktop only, prepended to every desktop-originated plan.
  seg('desk>street', 'Desk to street door, including lift and reception', 'street', 4),
];

/**
 * A generic fallback so an unrecognised interchange still costs something.
 * Spec 5.4's point is that the journey planner models none of these, so
 * assuming zero is the one answer guaranteed to be wrong.
 */
export const DEFAULT_INTERCHANGE_SECONDS = 3 * 60;

export function segmentFor(
  id: string,
  label: string,
  kind: SegmentKind,
  known: Map<string, WalkSegment>
): WalkSegment {
  const hit = known.get(id);
  if (hit) return hit;
  return {
    id,
    label,
    kind,
    seedSeconds: kind === 'street' ? 0 : DEFAULT_INTERCHANGE_SECONDS,
    samples: [],
  };
}
